import React, { useState, useEffect } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import {
  Button, Col, Row, Form, notification,
} from 'antd';
import './EstimateStatus.scss';
import EstimateStatusComponent from '../../sharedComponents/serviceStatusComponent/estimateStatusComponent/EstimateStatusComponent';
import { getUserType } from '../../../utils/users';
import {
  get, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { backendResponseCodes } from '../../../utils/responseMessages';

const EstimateStatus = (): JSX.Element => {
  interface FlowStep {
    id: number;
    stepName: string;
    date: string;
    reasonRefuses: string;
    descriptionRefuse: string;
    isDone: boolean;
  }

  const navigate = useNavigate();
  const routeMatch = useMatch('/estimate/status/:id');
  const [formInstance] = Form.useForm();
  const [userType] = useState<string | null>(getUserType());
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [statusesList, setStatusesList] = useState<Array<FlowStep>>([]);
  const [currentStatusIndex, setCurrentStatusIndex] = useState<number>(0);
  const [selectedStatus, setSelectedStatus] = useState<FlowStep | undefined>(undefined);
  const [canceled, setCanceled] = useState<boolean>(false);

  const loadStatusData = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    if (!routeMatch?.params?.id) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, houve um erro ao buscar os status.',
      });
      return;
    }

    setLoadingStatus(true);

    const requestReference: RequestWrapper = get({
      url: `/budget/listBudgetStatusFlow/${routeMatch.params.id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 13012): {
        // "Budget flow listed successfully", 13012
        const newStatusesList: Array<FlowStep> = response?.body.budgetStatusFlowOutputs;

        setStatusesList(newStatusesList);
        let nextId;

        const findLastIndex = (array: Array<FlowStep>): number => {
          let finalIndex = 0;

          array.forEach((arrValue, arrIndex) => {
            if (arrValue.date != null) {
              finalIndex = arrIndex;
            }
          });

          if ((finalIndex + 1) === array.length) {
            nextId = null;
          } else {
            nextId = array?.[finalIndex + 1]?.id;
          }

          return finalIndex;
        };

        const statIndex = findLastIndex(newStatusesList);
        setCurrentStatusIndex(statIndex);
        formInstance.setFieldsValue({ selectForm: nextId });

        setSelectedStatus(newStatusesList[statIndex + 1]);

        if (
          (newStatusesList[statIndex]?.stepName === 'Orçamento recusado'
            || newStatusesList[statIndex]?.stepName === 'Orçamento expirado')
          && newStatusesList[statIndex]?.isDone === true
        ) {
          setCanceled(true);
        } else {
          setCanceled(false);
        }

        setLoadingStatus(false);
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -14003):
        // "Budget not found", -14003
        notification.error({
          message: 'Error',
          description: backendResponseCodes.Error.Budget.BudgetNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Error',
          description: backendResponseCodes.Error.General.Forbidden
            .description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Error',
          description: 'Desculpe, houve um erro ao buscar os detalhes, por favor '
            + 'tente novamente mais tarde.',
        });
        break;
    }

    setLoadingStatus(false);
  };

  useEffect(() => {
    loadStatusData();

    return () => {
      setLoadingStatus(false);
    };
  }, []);

  return (
    <div className="EstimateStatus">
      <div className="page-title-container">
        {
          userType === 'COOPERCITRUS'
          && (
            <Row gutter={24}>
              <Col xs={{ span: 24 }} lg={{ span: 20 }}>
                <span className="page-title">Status do orçamento</span>
              </Col>
              <Col xs={{ span: 24 }} lg={{ span: 4 }}>
                <div className="buttons-container">
                  <Row gutter={[24, 24]}>
                    <Col xs={{ span: 24 }} md={{ span: 5 }}>
                      <Button
                        className="btn-previous"
                        onClick={
                          (): void => {
                            navigate('/service/estimates');
                          }
                        }
                      >
                        Voltar
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          )
        }
      </div>

      <div className="page-content">
        <EstimateStatusComponent
          loading={loadingStatus}
          statusesList={statusesList}
          currentStatusIndex={currentStatusIndex}
          canceled={canceled}
        />
      </div>
    </div>
  );
};

EstimateStatus.propTypes = {};
EstimateStatus.defaultProps = {};

export default EstimateStatus;
